import { useEffect, useMemo, useState } from 'react'
import { Bell, Globe } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuthStore, useStudioStore } from '@/store'
import { useTelegram } from '@/hooks/useTelegram'
import { Card, Badge } from '@/components/ui'
import { LoadingScreen } from '@/components/LoadingScreen'
import { Button } from '@/components/ui/Button'

interface NotificationConfigRow {
  notification_type_code: string
  is_enabled: boolean
  user_timezone?: string | null
  timezone_updated_at?: string | null
}

const PARTNER_REMINDER_CODE = 'partner_morning_reminder'

function formatTimezoneLabel(timezoneValue: string): string {
  if (!timezoneValue) return 'Не определён'
  return timezoneValue
}

export function NotificationSettingsPage() {
  const { user } = useAuthStore()
  const { studios } = useStudioStore()
  const { showAlert, hapticFeedback } = useTelegram()
  const [loading, setLoading] = useState(true)
  const [isEnabled, setIsEnabled] = useState(true)
  const [saving, setSaving] = useState(false)
  const [timezone, setTimezone] = useState<string>('')
  const [timezoneUpdatedAt, setTimezoneUpdatedAt] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    void loadSettings()
  }, [user?.id])

  const localTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    []
  )
  const displayTimezone = timezone || localTimezone
  const isManagementCompany = studios.some((studio) => studio.access_kind === 'management_company')

  const loadSettings = async () => {
    if (!user?.id) {
      setLoading(false)
      return
    }

    setLoading(true)
    setLoadError(null)
    let nextEnabled = true
    let nextTimezone = ''
    let nextTimezoneUpdatedAt: string | null = null

    try {
      const { data, error } = await (supabase as any).rpc('get_user_notification_config_v2', {
        p_user_id: user.id
      })

      if (error) {
        throw error
      }

      const rows = (data || []) as NotificationConfigRow[]
      const partnerRow = rows.find((row) => row.notification_type_code === PARTNER_REMINDER_CODE)
      if (partnerRow) {
        nextEnabled = Boolean(partnerRow.is_enabled)
      }
      if (rows.length > 0) {
        nextTimezone = rows[0].user_timezone || ''
        nextTimezoneUpdatedAt = rows[0].timezone_updated_at || null
      }
    } catch (error) {
      console.error('Failed to load notification settings:', error)
      setLoadError('Новые настройки уведомлений недоступны. Проверьте миграцию БД (Фаза 1).')
    }

    setIsEnabled(nextEnabled)
    setTimezone(nextTimezone)
    setTimezoneUpdatedAt(nextTimezoneUpdatedAt)
    setLoading(false)
  }

  const handleToggle = async (nextEnabled: boolean) => {
    if (!user?.id) return

    setSaving(true)
    setIsEnabled(nextEnabled)

    const { data: nextApiData, error: nextApiError } = await (supabase as any).rpc('toggle_partner_reminders_v2', {
      p_enabled: nextEnabled,
      p_user_id: user.id
    })

    setSaving(false)
    if (nextApiError || nextApiData?.success === false) {
      console.error('Failed to update partner reminder settings:', nextApiError || nextApiData)
      setIsEnabled(!nextEnabled)
      hapticFeedback.notification('error')
      showAlert('Не удалось сохранить изменения')
      return
    }

    hapticFeedback.notification('success')
  }

  if (loading) return <LoadingScreen />

  if (!user?.id) {
    return (
      <Card>
        <p className="text-sm text-muted-foreground">Пользователь не авторизован.</p>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Bell className="w-5 h-5 text-primary" />
        <h1 className="text-xl font-semibold">Настройки уведомлений</h1>
      </div>

      <Card>
        <h2 className="text-base font-semibold mb-2">Ежедневные напоминания</h2>
        <p className="text-sm text-muted-foreground mb-3">
          Получать ежедневное напоминание о заполнении данных.
        </p>

        <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
          <div>
            <p className="font-medium">Напоминания франчайзи</p>
            <p className="text-xs text-muted-foreground">Отправка один раз в день утром по вашему часовому поясу.</p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm whitespace-nowrap">
            <input
              type="checkbox"
              checked={isEnabled}
              disabled={saving}
              onChange={(e) => void handleToggle(e.target.checked)}
            />
            <span>{isEnabled ? 'Вкл' : 'Выкл'}</span>
          </label>
        </div>

        {!isManagementCompany && (
          <p className="mt-3 text-xs text-muted-foreground">
            Для франчайзи доступен только этот тип уведомлений.
          </p>
        )}
      </Card>

      <Card>
        <div className="flex items-center gap-2 mb-2">
          <Globe className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-base font-semibold">Часовой пояс</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Ваш часовой пояс: <span className="font-medium text-foreground">{formatTimezoneLabel(displayTimezone)}</span>
        </p>
        <p className="text-xs text-muted-foreground mt-2">
          Определяется автоматически при входе в приложение.
        </p>
        {timezoneUpdatedAt && (
          <p className="text-xs text-muted-foreground mt-2">
            Обновлен: {new Date(timezoneUpdatedAt).toLocaleString()}
          </p>
        )}
      </Card>

      {loadError && (
        <Card>
          <Badge variant="warning" text="Требуется миграция БД" />
          <p className="text-sm text-muted-foreground mt-2">{loadError}</p>
          <Button size="sm" variant="outline" className="mt-3" onClick={() => void loadSettings()}>
            Повторить
          </Button>
        </Card>
      )}
    </div>
  )
}
